import React, { useState } from "react";
import {
    Star,
    Award,
    Bolt,
    CircleCheck,
    TruckElectric,
    ShieldHalf,
} from "lucide-react";
import { useCheckout } from "@/hooks/useCheckout";
import { HotProductBadge } from "@/components/landing/HotProductBadge";

const DEFAULT_BENEFITS = [
    "Reduces Excessive Hair Fall",
    "Stimulates New Hair Growth",
    "Non-Greasy & Fast Absorbing",
    "100% Safe & Lab Certified",
];

export default function Hero({ product }) {
    const images = product?.images?.length ? product.images : [];
    const gallery = images.length
        ? [...images, ...Array(Math.max(0, 4 - images.length)).fill(images[0])]
        : [];
    const [active, setActive] = useState(0);
    const { openCheckout } = useCheckout();
    const topBundle = product?.bundles?.[0] || {};
    const benefits = (product?.benefits || []).slice(0, 4).map((b) => b.title);
    const bulletList = benefits.length ? benefits : DEFAULT_BENEFITS;
    const save = (topBundle.original_price || 0) - (topBundle.price || 0);

    return (
        <section
            data-testid="hero"
            id="top"
            className="relative pt-8 pb-16 md:pt-14 md:pb-24 overflow-hidden"
        >
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-forest-100/60 blur-3xl" />
                <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-gold-300/30 blur-3xl" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    <div className="lg:col-span-6 space-y-4 stagger">
                        <div className="relative bg-white rounded-3xl p-3 sm:p-4 border border-forest-100 shadow-xl overflow-hidden group">
                            <div className="absolute top-6 left-6 z-10 flex flex-col gap-2">
                                <span className="bg-forest-800 text-gold-400 text-[10px] sm:text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 border border-gold-500/40">
                                    <Award className="w-3.5 h-3.5" /> PCSIR Certified
                                </span>
                                {save > 0 && (
                                    <span className="bg-rose-600 text-white text-[10px] sm:text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                                        {Math.round((save / topBundle.original_price) * 100)}% OFF
                                    </span>
                                )}
                                <HotProductBadge productId={product?.id} testid="hero-hot-badge" />
                            </div>

                            <div className="rounded-2xl overflow-hidden bg-forest-50 relative flex items-center justify-center">
                                <img
                                    data-testid="hero-main-image"
                                    src={gallery[active]}
                                    alt={product?.name}
                                    onError={(e) => {
                                        e.currentTarget.src =
                                            "https://placehold.co/900x900/faf6ee/163721?text=Resham+Drops";
                                    }}
                                    className="w-full h-auto object-contain object-center transition-all duration-500 group-hover:scale-105"
                                />
                            </div>
                        </div>

                        {gallery.length > 1 && (
                            <div className="grid grid-cols-4 gap-3">
                                {gallery.slice(0, 4).map((g, i) => (
                                    <button
                                        key={i}
                                        data-testid={`gallery-thumb-${i}`}
                                        onClick={() => setActive(i)}
                                        className={`rounded-xl overflow-hidden aspect-square bg-white shadow-sm transition-all border-2 ${
                                            active === i
                                                ? "border-forest-700 opacity-100"
                                                : "border-transparent opacity-70 hover:opacity-100 hover:border-forest-300"
                                        }`}
                                    >
                                        <img
                                            src={g}
                                            alt="product thumbnail"
                                            onError={(e) => {
                                                e.currentTarget.style.visibility = "hidden";
                                            }}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-6 space-y-6 stagger">
                        <div className="flex items-center gap-3">
                            <div className="flex text-gold-500">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        className="w-4 h-4 fill-gold-500"
                                    />
                                ))}
                            </div>
                            <span className="text-xs font-semibold text-forest-700">
                                4.9/5.0 · 1,482+ Verified Reviews
                            </span>
                        </div>

                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-forest-800 leading-[1.05]">
                            {product?.hero_headline || product?.name}
                        </h1>

                        {product?.hero_subheadline && (
                            <p className="text-base sm:text-lg text-forest-900/70 leading-relaxed max-w-xl">
                                {product.hero_subheadline}
                            </p>
                        )}

                        {product?.tagline && (
                            <div className="bg-forest-50/80 p-4 rounded-2xl border border-forest-100 flex items-center justify-between gap-3">
                                <div>
                                    <span className="text-[10px] font-bold text-forest-700 uppercase tracking-widest block">
                                        {product.category || "Flagship Serum"}
                                    </span>
                                    <span className="font-bold text-forest-900 text-base sm:text-lg">
                                        {product.name} · {product.tagline}
                                    </span>
                                </div>
                            </div>
                        )}

                        {topBundle.price && (
                            <div className="p-5 rounded-2xl bg-white border border-forest-100 shadow-sm flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <span className="text-[10px] font-bold text-forest-700/50 uppercase tracking-widest block">
                                        Launch Promo Price
                                    </span>
                                    <div className="flex items-baseline gap-3">
                                        <span className="text-3xl sm:text-4xl font-extrabold text-forest-800">
                                            {topBundle.price.toLocaleString()} PKR
                                        </span>
                                        {topBundle.original_price > topBundle.price && (
                                            <span className="text-base sm:text-lg text-forest-900/40 line-through">
                                                {topBundle.original_price.toLocaleString()} PKR
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {save > 0 && (
                                    <div className="text-right">
                                        <span className="inline-flex items-center gap-1 bg-forest-50 text-forest-800 text-[10px] sm:text-xs font-bold px-3 py-1 rounded-full border border-forest-200">
                                            <Bolt className="w-3.5 h-3.5 text-gold-500 fill-gold-500" />
                                            You Save {save.toLocaleString()} PKR
                                        </span>
                                        <span className="block text-[11px] text-forest-700/70 mt-1 font-medium">
                                            Free shipping across Pakistan
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 text-sm font-medium text-forest-800">
                            {bulletList.map((b, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <CircleCheck className="w-5 h-5 text-forest-600 fill-forest-100" />
                                    <span>{b}</span>
                                </div>
                            ))}
                        </div>

                        <div className="space-y-3 pt-1">
                            <button
                                type="button"
                                onClick={openCheckout}
                                data-testid="hero-cta"
                                className="animate-soft-pulse w-full bg-forest-800 hover:bg-forest-900 text-white text-center font-bold text-base sm:text-lg py-4 px-6 rounded-2xl shadow-xl transition-all flex items-center justify-center gap-3 border-2 border-gold-500"
                            >
                                <TruckElectric className="w-6 h-6 text-gold-400" />
                                <span>ORDER NOW · CASH ON DELIVERY</span>
                            </button>
                            <p className="text-center text-xs text-forest-700/70 flex items-center justify-center gap-2">
                                <ShieldHalf className="w-4 h-4 text-forest-700" />
                                Pay cash when the courier arrives at your door.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
